import { create } from 'zustand';
import { subscriptionService, SubscriptionStatus } from '../services/subscriptionService';

interface SubscriptionState {
  status: SubscriptionStatus | null;
  loading: boolean;
  error: string | null;
  
  // Actions
  fetchStatus: () => Promise<void>;
  useFreeTrial: () => Promise<boolean>;
  useGame: () => Promise<boolean>;
  reset: () => void;
}

const initialStatus: SubscriptionStatus = {
  hasUsedFreeTrial: false,
  freeTrialUsedAt: null,
  gamesRemaining: 0,
  totalGamesPurchased: 0,
  canPlay: true,
  canPlayFree: true,
};

export const useSubscriptionStore = create<SubscriptionState>((set, get) => ({
  status: null,
  loading: false,
  error: null,

  fetchStatus: async () => {
    try {
      set({ loading: true, error: null });
      const status = await subscriptionService.getStatus();
      set({ status, loading: false });
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
      // On error, assume free trial is available (new user)
      set({ 
        status: initialStatus, 
        loading: false,
        error: 'فشل في جلب حالة الاشتراك'
      });
    }
  },

  useFreeTrial: async () => {
    try {
      set({ loading: true, error: null });
      const result = await subscriptionService.useFreeTrial();
      
      // Update the local status
      const currentStatus = get().status;
      if (currentStatus) {
        set({
          status: {
            ...currentStatus,
            hasUsedFreeTrial: true,
            freeTrialUsedAt: result.freeTrialUsedAt,
            canPlayFree: false,
          },
          loading: false,
        });
      }
      return true;
    } catch (error: any) {
      console.error('Failed to use free trial:', error);
      // Even if API fails, allow playing for free trial (for development/testing)
      const currentStatus = get().status;
      if (currentStatus) {
        set({
          status: {
            ...currentStatus,
            hasUsedFreeTrial: true,
            canPlayFree: false,
          },
          loading: false,
        });
      }
      // Return true to allow playing anyway
      return true;
    }
  },

  useGame: async () => {
    try {
      set({ loading: true, error: null });
      const result = await subscriptionService.useGame();
      
      // Update the local status
      const currentStatus = get().status;
      if (currentStatus) {
        set({
          status: {
            ...currentStatus,
            gamesRemaining: result.gamesRemaining,
            canPlay: result.gamesRemaining > 0 || !currentStatus.hasUsedFreeTrial,
          },
          loading: false,
        });
      }
      return true;
    } catch (error: any) {
      console.error('Failed to use game:', error);
      set({ 
        loading: false,
        error: error.response?.data?.message || 'فشل في خصم اللعبة'
      });
      return false;
    }
  },

  reset: () => {
    set({ status: null, loading: false, error: null });
  },
}));
